import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  EFConnectableSide,
  EFConnectionBehavior,
  EFConnectionType,
  EFMarkerType,
  FFlowModule,
  type FCreateConnectionEvent,
  type FReassignConnectionEvent,
  provideFFlow,
  withFCanvas,
} from '@foblex/flow';
import { NgIcon, provideIcons } from '@ng-icons/core';
import {
  heroAdjustmentsHorizontal,
  heroArrowPath,
  heroBolt,
  heroChatBubbleLeftRight,
  heroCheckCircle,
  heroClock,
  heroCodeBracket,
  heroCursorArrowRays,
  heroExclamationTriangle,
  heroNoSymbol,
  heroPlayCircle,
  heroPlus,
  heroQuestionMarkCircle,
  heroShieldCheck,
  heroSparkles,
  heroSquaresPlus,
  heroStopCircle,
  heroTrash,
  heroUserGroup,
  heroWrenchScrewdriver,
} from '@ng-icons/heroicons/outline';
import { catchError, forkJoin, map, of, switchMap, type Observable } from 'rxjs';
import type { AgentToolDefinition } from '../../../core/api/generated/models/AgentToolDefinition';
import type { Automation } from '../../../core/api/generated/models/Automation';
import type { AutomationStepStepTypeEnumKey } from '../../../core/api/generated/models/AutomationStep';
import type {
  AutomationTrigger,
  AutomationTriggerTriggerTypeEnumKey,
} from '../../../core/api/generated/models/AutomationTrigger';
import type { ChannelAccount } from '../../../core/api/generated/models/ChannelAccount';
import {
  ToastrService,
  UiBadge,
  UiButton,
  UiHint,
  UiIconButton,
  UiInput,
  UiLabel,
  UiNativeSelect,
  UiOptionRow,
  UiOptionRowCopy,
  UiOptionRowIcon,
  UiPanelCard,
  UiSwitch,
  UiSwitchThumb,
  UiTextarea,
} from '../../../shared/ui';
import {
  AgentsApiService,
  type AutomationDefinitionInput,
  type AutomationTriggerInput,
} from '../agents-api.service';

type JsonObject = Record<string, unknown>;
type FlowPoint = { x: number; y: number };
type AutomationNodeKind = 'trigger' | 'condition' | 'action' | 'handoff' | 'wait' | 'end';
type AutomationStatus = 'draft' | 'active' | 'paused' | 'archived';
type AutomationTemplateGroup = 'trigger' | 'tool' | 'condition' | 'flow' | 'system';
type BusinessFieldType = 'text' | 'number' | 'boolean' | 'date' | 'enum' | 'json';
type NodeFieldType = 'text' | 'textarea' | 'select' | 'combobox' | 'boolean' | 'number';
type FieldOptionSource =
  | 'businessFields'
  | 'channelAccounts'
  | 'conditionModes'
  | 'conditionOperators'
  | 'conditionValues'
  | 'tools'
  | 'triggerTypes'
  | 'handoffTargets';

type SelectOption = {
  value: string;
  label: string;
  description?: string;
};

type BusinessField = SelectOption & {
  group: 'conversation' | 'message' | 'contact' | 'lead' | 'deal' | 'channel' | 'system' | 'automation';
  type: BusinessFieldType;
  options?: readonly SelectOption[];
};

type NodeField = {
  key: string;
  label: string;
  value: string;
  type: NodeFieldType;
  placeholder?: string;
  optionSource?: FieldOptionSource;
  helper?: string;
  required?: boolean;
};

type AutomationFlowNode = {
  id: string;
  stepKey: string;
  kind: AutomationNodeKind;
  icon: string;
  title: string;
  eyebrow: string;
  description: string;
  stepType: AutomationStepStepTypeEnumKey | 'trigger';
  position: FlowPoint;
  inputId?: string;
  outputId?: string;
  badges: readonly string[];
  settings: readonly NodeField[];
  outputFields: readonly string[];
  policies: readonly string[];
};

type AutomationConnection = {
  id: string;
  from: string;
  to: string;
  branchKey?: string | null;
};

type AutomationTemplate = Omit<AutomationFlowNode, 'id' | 'stepKey' | 'position' | 'inputId' | 'outputId'> & {
  templateId: string;
  library: 'Gatilho' | 'Sistema' | 'Fluxo' | 'Integracao';
};

type WorkflowIssue = {
  nodeId?: string;
  tone: 'danger' | 'warning';
  message: string;
};

type AutomationTriggerRecord = AutomationTrigger & {
  name?: string | null;
  conversationContextPolicy?: JsonObject;
};

type AutomationRecord = Automation & {
  description?: string | null;
  triggerDefaultsPolicy?: JsonObject;
  defaultContextPolicy?: JsonObject;
  rateLimitPolicy?: JsonObject;
};

const AUTOMATION_KEY = 'qualificar-lead';
const AUTOMATION_NAME = 'Qualificar lead';

const STATUS_LABELS: Record<AutomationStatus, string> = {
  draft: 'Rascunho',
  active: 'Ativa',
  paused: 'Pausada',
  archived: 'Arquivada',
};

const FIELD_GROUP_LABELS: Record<BusinessField['group'], string> = {
  conversation: 'Conversa',
  message: 'Mensagem',
  contact: 'Contato',
  lead: 'Lead',
  deal: 'Oportunidade',
  channel: 'Canal',
  system: 'Sistema',
  automation: 'Automacao',
};

const TRIGGER_TYPE_OPTIONS: readonly SelectOption[] = [
  { value: 'conversation_assignment', label: 'Assignment da missao', description: 'A missao envia a conversa para a automacao.' },
  { value: 'conversation_message', label: 'Mensagem recebida', description: 'Entrada nova no canal selecionado.' },
  { value: 'manual', label: 'Manual ou API', description: 'Execucao disparada por usuario, API ou teste.' },
];

const CONDITION_MODE_OPTIONS: readonly SelectOption[] = [
  { value: 'deterministic', label: 'Regra por campo', description: 'Compara campos usando operador tipado.' },
  { value: 'llm', label: 'LLM descritiva', description: 'Usa instrucao em linguagem natural para rotear.' },
];

const CONDITION_OPERATORS: Record<BusinessFieldType, readonly SelectOption[]> = {
  text: [
    { value: 'contains', label: 'contem' },
    { value: 'not_contains', label: 'nao contem' },
    { value: 'equals', label: 'e igual a' },
    { value: 'is_empty', label: 'esta vazio' },
    { value: 'is_not_empty', label: 'esta preenchido' },
  ],
  number: [
    { value: 'equals', label: 'e igual a' },
    { value: 'gt', label: 'maior que' },
    { value: 'gte', label: 'maior ou igual' },
    { value: 'lt', label: 'menor que' },
    { value: 'lte', label: 'menor ou igual' },
  ],
  boolean: [
    { value: 'is_true', label: 'e verdadeiro' },
    { value: 'is_false', label: 'e falso' },
  ],
  date: [
    { value: 'before', label: 'antes de' },
    { value: 'after', label: 'depois de' },
    { value: 'between', label: 'entre' },
    { value: 'is_empty', label: 'sem data' },
  ],
  enum: [
    { value: 'equals', label: 'e igual a' },
    { value: 'not_equals', label: 'e diferente de' },
    { value: 'in', label: 'esta em' },
    { value: 'not_in', label: 'nao esta em' },
  ],
  json: [
    { value: 'exists', label: 'existe' },
    { value: 'json_path_equals', label: 'JSON path igual a' },
    { value: 'json_path_exists', label: 'JSON path existe' },
  ],
};

const BOOLEAN_VALUE_OPTIONS: readonly SelectOption[] = [
  { value: 'true', label: 'Verdadeiro' },
  { value: 'false', label: 'Falso' },
];

const HANDOFF_TARGET_OPTIONS: readonly SelectOption[] = [
  { value: 'team.support', label: 'Equipe de atendimento' },
  { value: 'team.sales', label: 'Equipe comercial' },
  { value: 'agent.current', label: 'Agente atual' },
  { value: 'human.available', label: 'Atendente disponivel' },
];

const BASE_BUSINESS_FIELDS: readonly BusinessField[] = [
  { value: 'conversation.id', label: 'ID da conversa', group: 'conversation', type: 'text' },
  {
    value: 'conversation.status',
    label: 'Status da conversa',
    group: 'conversation',
    type: 'enum',
    options: [
      { value: 'open', label: 'Aberta' },
      { value: 'waiting', label: 'Aguardando cliente' },
      { value: 'resolved', label: 'Resolvida' },
    ],
  },
  {
    value: 'conversation.priority',
    label: 'Prioridade',
    group: 'conversation',
    type: 'enum',
    options: [
      { value: 'low', label: 'Baixa' },
      { value: 'normal', label: 'Normal' },
      { value: 'high', label: 'Alta' },
      { value: 'urgent', label: 'Urgente' },
    ],
  },
  { value: 'conversation.intent', label: 'Intencao detectada', group: 'conversation', type: 'text' },
  { value: 'conversation.summary', label: 'Resumo da conversa', group: 'conversation', type: 'text' },
  { value: 'message.text', label: 'Texto da mensagem', group: 'message', type: 'text' },
  {
    value: 'message.channel',
    label: 'Canal da mensagem',
    group: 'message',
    type: 'enum',
    options: [
      { value: 'whatsapp', label: 'WhatsApp' },
      { value: 'instagram_dm', label: 'Instagram DM' },
      { value: 'webchat', label: 'Webchat' },
      { value: 'email', label: 'E-mail' },
      { value: 'sms', label: 'SMS' },
    ],
  },
  { value: 'message.received_at', label: 'Data da mensagem', group: 'message', type: 'date' },
  { value: 'contact.id', label: 'ID do contato', group: 'contact', type: 'text' },
  { value: 'contact.name', label: 'Nome do contato', group: 'contact', type: 'text' },
  { value: 'contact.email', label: 'E-mail do contato', group: 'contact', type: 'text' },
  { value: 'contact.phone', label: 'Telefone do contato', group: 'contact', type: 'text' },
  { value: 'contact.blocked', label: 'Contato bloqueado', group: 'contact', type: 'boolean' },
  {
    value: 'contact.lifecycle_stage',
    label: 'Estagio do contato',
    group: 'contact',
    type: 'enum',
    options: [
      { value: 'lead', label: 'Lead' },
      { value: 'qualified_lead', label: 'Lead qualificado' },
      { value: 'customer', label: 'Cliente' },
      { value: 'inactive', label: 'Inativo' },
    ],
  },
  { value: 'lead.score', label: 'Score do lead', group: 'lead', type: 'number' },
  {
    value: 'lead.intent',
    label: 'Interesse do lead',
    group: 'lead',
    type: 'enum',
    options: [
      { value: 'sales', label: 'Compra' },
      { value: 'support', label: 'Suporte' },
      { value: 'human', label: 'Atendimento humano' },
    ],
  },
  { value: 'lead.product_interest', label: 'Produto de interesse', group: 'lead', type: 'text' },
  { value: 'lead.email', label: 'E-mail coletado', group: 'lead', type: 'text' },
  { value: 'deal.id', label: 'ID da oportunidade', group: 'deal', type: 'text' },
  { value: 'deal.amount', label: 'Valor da oportunidade', group: 'deal', type: 'number' },
  {
    value: 'deal.stage',
    label: 'Etapa da oportunidade',
    group: 'deal',
    type: 'enum',
    options: [
      { value: 'new', label: 'Nova' },
      { value: 'qualified', label: 'Qualificada' },
      { value: 'proposal', label: 'Proposta' },
      { value: 'won', label: 'Ganha' },
      { value: 'lost', label: 'Perdida' },
    ],
  },
  { value: 'channel.account_id', label: 'Conta do canal', group: 'channel', type: 'text' },
  {
    value: 'channel.kind',
    label: 'Tipo do canal',
    group: 'channel',
    type: 'enum',
    options: [
      { value: 'whatsapp', label: 'WhatsApp' },
      { value: 'instagram_dm', label: 'Instagram DM' },
      { value: 'webchat', label: 'Webchat' },
      { value: 'email', label: 'E-mail' },
      { value: 'sms', label: 'SMS' },
    ],
  },
  { value: 'channel.window_open', label: 'Janela de atendimento aberta', group: 'channel', type: 'boolean' },
  { value: 'system.now', label: 'Data atual', group: 'system', type: 'date' },
  { value: 'system.business_hours_open', label: 'Horario comercial aberto', group: 'system', type: 'boolean' },
];

const TEMPLATE_GROUPS: readonly {
  id: AutomationTemplateGroup;
  label: string;
  icon: string;
  description: string;
}[] = [
  { id: 'trigger', label: 'Gatilhos', icon: 'heroCursorArrowRays', description: 'Eventos que iniciam a automacao.' },
  { id: 'tool', label: 'Ferramentas', icon: 'heroWrenchScrewdriver', description: 'CRM, webhooks e integracoes.' },
  { id: 'condition', label: 'Condicoes', icon: 'heroQuestionMarkCircle', description: 'Regras tipadas ou LLM.' },
  { id: 'flow', label: 'Fluxo', icon: 'heroBolt', description: 'Pausas, agente e controle.' },
  { id: 'system', label: 'Sistema', icon: 'heroShieldCheck', description: 'Handoff, bloqueio, CSAT e fechamento.' },
];

const TRIGGER_NODE_TEMPLATE: AutomationTemplate = {
  templateId: 'trigger.conversation',
  library: 'Gatilho',
  kind: 'trigger',
  icon: 'heroCursorArrowRays',
  title: 'Entrada da automacao',
  eyebrow: 'gatilho',
  description: 'Define qual canal e evento iniciam a automacao.',
  stepType: 'trigger',
  badges: ['Canal', 'Entrada'],
  settings: [
    {
      key: 'channelAccountId',
      label: 'Canal',
      value: '',
      type: 'select',
      optionSource: 'channelAccounts',
      required: true,
      helper: 'Selecione a conta/canal que pode iniciar este workflow.',
    },
    {
      key: 'triggerType',
      label: 'Tipo de gatilho',
      value: 'conversation_assignment',
      type: 'select',
      optionSource: 'triggerTypes',
      required: true,
    },
    {
      key: 'assignmentKey',
      label: 'Assignment',
      value: 'automation.qualificar_lead',
      type: 'text',
      placeholder: 'automation.nome_do_fluxo',
    },
    {
      key: 'filter',
      label: 'Filtro',
      value: 'Conversa aberta e contato nao bloqueado',
      type: 'textarea',
      placeholder: 'Descreva quando este gatilho deve valer.',
    },
  ],
  outputFields: ['conversation.id', 'conversation.status', 'contact.id', 'message.text', 'message.channel', 'channel.account_id'],
  policies: ['Um canal ativo so pode apontar para um alvo de automacao/agente.'],
};

const AUTOMATION_TEMPLATES: readonly AutomationTemplate[] = [
  TRIGGER_NODE_TEMPLATE,
  {
    templateId: 'flow.condition',
    library: 'Fluxo',
    kind: 'condition',
    icon: 'heroQuestionMarkCircle',
    title: 'Condicao',
    eyebrow: 'branch',
    description: 'Direciona por regra de campo ou por criterio descritivo com LLM.',
    stepType: 'condition',
    badges: ['Deterministico', 'LLM opcional'],
    settings: [
      { key: 'mode', label: 'Modo', value: 'deterministic', type: 'select', optionSource: 'conditionModes' },
      { key: 'field', label: 'Campo', value: 'message.text', type: 'combobox', optionSource: 'businessFields' },
      { key: 'operator', label: 'Operador', value: 'contains', type: 'select', optionSource: 'conditionOperators' },
      { key: 'value', label: 'Valor', value: '', type: 'combobox', optionSource: 'conditionValues', placeholder: 'Valor esperado' },
      {
        key: 'llmPrompt',
        label: 'Direcionamento',
        value: 'Classifique a conversa e escolha o melhor proximo passo usando apenas o contexto disponivel.',
        type: 'textarea',
      },
    ],
    outputFields: ['branch.selected'],
    policies: ['Sempre configure um caminho de fallback.'],
  },
  {
    templateId: 'flow.collect',
    library: 'Fluxo',
    kind: 'action',
    icon: 'heroSquaresPlus',
    title: 'Coletar informacoes',
    eyebrow: 'coleta',
    description: 'Cria campos dinamicos que o agente deve coletar naturalmente.',
    stepType: 'agent_run',
    badges: ['Campos dinamicos', 'IA'],
    settings: [
      { key: 'fields', label: 'Campos', value: 'nome, email, produto_de_interesse', type: 'text' },
      {
        key: 'instruction',
        label: 'Instrucao',
        value: 'Colete apenas o que estiver faltando, um dado por vez, e confirme naturalmente.',
        type: 'textarea',
      },
    ],
    outputFields: ['lead.name', 'lead.email', 'lead.product_interest'],
    policies: ['Nao repetir pergunta ja respondida.', 'Validar email antes de seguir.'],
  },
  {
    templateId: 'integration.tool',
    library: 'Integracao',
    kind: 'action',
    icon: 'heroWrenchScrewdriver',
    title: 'Executar ferramenta',
    eyebrow: 'tool',
    description: 'Chama uma tool habilitada e entrega o retorno para os proximos blocos.',
    stepType: 'integration_action',
    badges: ['Tool', 'Input/Output'],
    settings: [
      { key: 'toolId', label: 'Ferramenta', value: '', type: 'select', optionSource: 'tools' },
      { key: 'toolInput', label: 'Campo de entrada', value: 'contact.phone', type: 'combobox', optionSource: 'businessFields' },
      { key: 'outputPrefix', label: 'Prefixo de saida', value: 'tool.result', type: 'text' },
    ],
    outputFields: ['tool.result.status', 'tool.result.body'],
    policies: ['Mascara dados sensiveis no log.', 'Valida schema de saida.'],
  },
  {
    templateId: 'flow.wait',
    library: 'Fluxo',
    kind: 'wait',
    icon: 'heroClock',
    title: 'Esperar evento',
    eyebrow: 'wait',
    description: 'Pausa ate nova mensagem, evento de sistema ou timeout.',
    stepType: 'wait',
    badges: ['Controle'],
    settings: [
      { key: 'until', label: 'Aguardar', value: 'Nova mensagem do contato', type: 'text' },
      { key: 'timeout', label: 'Timeout (min)', value: '30', type: 'number' },
    ],
    outputFields: ['message.text', 'wait.timed_out'],
    policies: ['Timeout sempre definido.'],
  },
  {
    templateId: 'flow.agent',
    library: 'Fluxo',
    kind: 'action',
    icon: 'heroSparkles',
    title: 'Executar agente',
    eyebrow: 'agent_run',
    description: 'Entrega a etapa ao agente para escrever, decidir ou preencher campos.',
    stepType: 'agent_run',
    badges: ['IA', 'Assignment'],
    settings: [
      { key: 'task', label: 'Tarefa', value: 'Responder com base na missao, contexto e contratos do fluxo.', type: 'textarea' },
    ],
    outputFields: ['agent.response', 'agent.intent', 'agent.filled_fields'],
    policies: ['Saida validada por schema.'],
  },
  {
    templateId: 'system.handoff',
    library: 'Sistema',
    kind: 'handoff',
    icon: 'heroUserGroup',
    title: 'Transferir atendimento',
    eyebrow: 'handoff',
    description: 'Transfere a conversa para agente, atendente ou equipe com contexto.',
    stepType: 'handoff',
    badges: ['Sistema', 'Assignment'],
    settings: [
      { key: 'target', label: 'Destino', value: 'team.support', type: 'select', optionSource: 'handoffTargets' },
      { key: 'reason', label: 'Motivo', value: 'Cliente precisa de atendimento humano.', type: 'textarea' },
      { key: 'summary', label: 'Resumo', value: 'Resumo: {{conversation.summary}}', type: 'textarea' },
    ],
    outputFields: ['assignment.owner_type', 'assignment.team_id', 'handoff.reason'],
    policies: ['Preserva contexto da conversa.', 'Exige motivo rastreavel.'],
  },
  {
    templateId: 'system.csat',
    library: 'Sistema',
    kind: 'action',
    icon: 'heroCheckCircle',
    title: 'Coletar CSAT',
    eyebrow: 'pesquisa',
    description: 'Envia pesquisa de satisfacao e grava resposta no historico.',
    stepType: 'send_message',
    badges: ['Sistema', 'Metrica'],
    settings: [
      { key: 'question', label: 'Pergunta', value: 'De 1 a 5, como foi seu atendimento?', type: 'textarea' },
      { key: 'field', label: 'Campo de saida', value: 'conversation.csat_score', type: 'combobox', optionSource: 'businessFields' },
    ],
    outputFields: ['conversation.csat_score', 'conversation.csat_comment'],
    policies: ['Executa uma vez por conversa.'],
  },
  {
    templateId: 'system.block',
    library: 'Sistema',
    kind: 'action',
    icon: 'heroNoSymbol',
    title: 'Bloquear contato',
    eyebrow: 'seguranca',
    description: 'Bloqueia o contato e registra motivo operacional.',
    stepType: 'update_crm',
    badges: ['Sistema', 'Sensivel'],
    settings: [
      { key: 'reason', label: 'Motivo', value: 'Spam, abuso ou opt-out explicito.', type: 'textarea' },
    ],
    outputFields: ['contact.blocked', 'contact.block_reason'],
    policies: ['Requer permissao administrativa.', 'Gera evento de auditoria.'],
  },
  {
    templateId: 'system.end',
    library: 'Sistema',
    kind: 'end',
    icon: 'heroStopCircle',
    title: 'Encerrar conversa',
    eyebrow: 'final',
    description: 'Marca a conversa como resolvida e encerra o fluxo.',
    stepType: 'end',
    badges: ['Sistema', 'Terminal'],
    settings: [
      { key: 'finalMessage', label: 'Mensagem final', value: 'Tudo certo. Posso ajudar em mais alguma coisa?', type: 'textarea' },
      { key: 'sendCsat', label: 'Enviar CSAT', value: 'true', type: 'boolean' },
    ],
    outputFields: ['conversation.status', 'conversation.resolution'],
    policies: ['Nao encerra se houver handoff pendente.'],
  },
];

const INITIAL_NODES: readonly AutomationFlowNode[] = [
  createNodeFromTemplate(TRIGGER_NODE_TEMPLATE, {
    id: 'trigger-conversation',
    stepKey: 'trigger',
    position: { x: 40, y: 150 },
    outputId: 'trigger-conversation-out',
  }),
  createNodeFromTemplate(AUTOMATION_TEMPLATES[1], {
    id: 'condition-intent',
    stepKey: 'identificar-necessidade',
    position: { x: 360, y: 150 },
    inputId: 'condition-intent-in',
    outputId: 'condition-intent-out',
    title: 'Identificar necessidade',
    outputFields: ['lead.intent', 'branch.selected'],
  }),
  createNodeFromTemplate(AUTOMATION_TEMPLATES[2], {
    id: 'collect-fields',
    stepKey: 'coletar-campos',
    position: { x: 680, y: 40 },
    inputId: 'collect-fields-in',
    outputId: 'collect-fields-out',
  }),
  createNodeFromTemplate(AUTOMATION_TEMPLATES[3], {
    id: 'tool-crm',
    stepKey: 'consultar-crm',
    position: { x: 1000, y: 40 },
    inputId: 'tool-crm-in',
    outputId: 'tool-crm-out',
    title: 'Consultar CRM',
  }),
  createNodeFromTemplate(AUTOMATION_TEMPLATES[6], {
    id: 'handoff-sales',
    stepKey: 'transferir-comercial',
    position: { x: 1000, y: 300 },
    inputId: 'handoff-sales-in',
    outputId: 'handoff-sales-out',
    title: 'Transferir para comercial',
  }),
  createNodeFromTemplate(AUTOMATION_TEMPLATES[9], {
    id: 'close-conversation',
    stepKey: 'encerrar-conversa',
    position: { x: 680, y: 300 },
    inputId: 'close-conversation-in',
  }),
];

const INITIAL_CONNECTIONS: readonly AutomationConnection[] = [
  { id: 'c-trigger-condition', from: 'trigger-conversation-out', to: 'condition-intent-in' },
  { id: 'c-condition-collect', from: 'condition-intent-out', to: 'collect-fields-in' },
  { id: 'c-collect-tool', from: 'collect-fields-out', to: 'tool-crm-in' },
  { id: 'c-tool-handoff', from: 'tool-crm-out', to: 'handoff-sales-in' },
  { id: 'c-handoff-close', from: 'handoff-sales-out', to: 'close-conversation-in' },
];

@Component({
  selector: 'app-automation-builder-page',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FFlowModule,
    NgClass,
    NgIcon,
    UiBadge,
    UiButton,
    UiHint,
    UiIconButton,
    UiInput,
    UiLabel,
    UiNativeSelect,
    UiOptionRow,
    UiOptionRowCopy,
    UiOptionRowIcon,
    UiPanelCard,
    UiSwitch,
    UiSwitchThumb,
    UiTextarea,
  ],
  providers: [
    provideFFlow(withFCanvas()),
    provideIcons({
      heroAdjustmentsHorizontal,
      heroArrowPath,
      heroBolt,
      heroChatBubbleLeftRight,
      heroCheckCircle,
      heroClock,
      heroCodeBracket,
      heroCursorArrowRays,
      heroExclamationTriangle,
      heroNoSymbol,
      heroPlayCircle,
      heroPlus,
      heroQuestionMarkCircle,
      heroShieldCheck,
      heroSparkles,
      heroSquaresPlus,
      heroStopCircle,
      heroTrash,
      heroUserGroup,
      heroWrenchScrewdriver,
    }),
  ],
  templateUrl: './automation-builder.page.html',
  styleUrl: './automation-builder.page.css',
})
export class AutomationBuilderPage {
  private readonly api = inject(AgentsApiService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly toast = inject(ToastrService);

  readonly connectionType = EFConnectionType.SEGMENT;
  readonly connectionBehavior = EFConnectionBehavior.FIXED;
  readonly inputSide = EFConnectableSide.LEFT;
  readonly outputSide = EFConnectableSide.RIGHT;
  readonly markerEnd = EFMarkerType.END;
  readonly canvasPosition: FlowPoint = { x: 0, y: 0 };
  readonly canvasScale = 0.78;
  readonly templateGroups = TEMPLATE_GROUPS;
  readonly templates = AUTOMATION_TEMPLATES;

  readonly automationId = signal<string | null>(null);
  readonly automationTriggerId = signal<string | null>(null);
  readonly automationName = signal(AUTOMATION_NAME);
  readonly status = signal<AutomationStatus>('draft');
  readonly loading = signal(false);
  readonly saving = signal(false);
  readonly runningTest = signal(false);
  readonly savedAutomations = signal<AutomationRecord[]>([]);
  readonly automationTriggers = signal<AutomationTriggerRecord[]>([]);
  readonly channelAccounts = signal<ChannelAccount[]>([]);
  readonly tools = signal<AgentToolDefinition[]>([]);
  readonly nodes = signal<readonly AutomationFlowNode[]>(INITIAL_NODES);
  readonly connections = signal<readonly AutomationConnection[]>(INITIAL_CONNECTIONS);
  readonly selectedNodeId = signal(INITIAL_NODES[1].id);
  readonly dirty = signal(false);
  readonly librarySearch = signal('');
  readonly blockMenuOpen = signal(false);
  readonly selectedTemplateGroup = signal<AutomationTemplateGroup>('condition');
  readonly expandedNodeIds = signal<readonly string[]>([]);
  readonly validationIssues = signal<readonly WorkflowIssue[]>([]);

  readonly selectedNode = computed(() => this.nodes().find((node) => node.id === this.selectedNodeId()) ?? null);
  readonly statusLabel = computed(() => STATUS_LABELS[this.status()]);
  readonly visibleTemplates = computed(() => {
    const query = normalize(this.librarySearch());
    const group = this.selectedTemplateGroup();
    const templates = this.templates.filter((template) => templateGroup(template) === group);
    if (!query) return templates;

    return templates.filter((template) =>
      normalize(`${template.title} ${template.eyebrow} ${template.description} ${template.badges.join(' ')}`).includes(query),
    );
  });
  readonly businessFields = computed(() => {
    const byValue = new Map<string, BusinessField>();
    for (const field of BASE_BUSINESS_FIELDS) byValue.set(field.value, field);
    for (const node of this.nodes()) {
      for (const fieldKey of this.outputFieldsForNode(node)) {
        if (!byValue.has(fieldKey)) {
          byValue.set(fieldKey, {
            value: fieldKey,
            label: humanizeFieldKey(fieldKey),
            group: 'automation',
            type: inferBusinessFieldType(fieldKey),
            description: `Gerado por ${node.title}`,
          });
        }
      }
    }
    return [...byValue.values()].sort((left, right) =>
      `${FIELD_GROUP_LABELS[left.group]} ${left.label}`.localeCompare(`${FIELD_GROUP_LABELS[right.group]} ${right.label}`, 'pt-BR'),
    );
  });
  readonly flowSummary = computed(() => {
    const steps = this.nodes().filter((node) => node.kind !== 'trigger').length;
    const invalid = this.validationIssues().filter((issue) => issue.tone === 'danger').length;
    const selectedChannel = this.selectedChannelAccount();
    return [
      { label: 'Blocos', value: String(steps) },
      { label: 'Conexoes', value: String(this.connections().length) },
      { label: 'Canal', value: selectedChannel?.label || selectedChannel?.externalSenderId || 'Nao definido' },
      { label: 'Pendencias', value: String(invalid) },
    ];
  });

  constructor() {
    this.loadWorkspace();
  }

  loadWorkspace(): void {
    this.loading.set(true);
    forkJoin({
      automations: this.api.listAutomations().pipe(catchError(() => of({ data: [], meta: { page: 1, pageSize: 0, total: 0, totalPages: 0 } }))),
      channelAccounts: this.api.listAllChannelAccounts().pipe(catchError(() => of([] as ChannelAccount[]))),
      tools: this.api.listTools().pipe(catchError(() => of({ data: [], meta: { page: 1, pageSize: 0, total: 0, totalPages: 0 } }))),
    })
      .pipe(
        switchMap((result) => {
          const automation = (result.automations.data as AutomationRecord[]).find((item) => item.key === AUTOMATION_KEY)
            ?? (result.automations.data as AutomationRecord[])[0]
            ?? null;
          if (!automation) {
            return of({ ...result, automation, triggers: { data: [] as AutomationTriggerRecord[] } });
          }
          return this.api.listAutomationTriggers(automation.id).pipe(
            catchError(() => of({ data: [] as AutomationTriggerRecord[] })),
            map((triggers) => ({ ...result, automation, triggers })),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((result) => {
        this.savedAutomations.set(result.automations.data as AutomationRecord[]);
        this.channelAccounts.set(result.channelAccounts.filter((account) => account.status === 'active'));
        this.tools.set(result.tools.data.filter((tool) => tool.status !== 'disabled'));
        this.automationTriggers.set(result.triggers.data as AutomationTriggerRecord[]);
        if (result.automation) this.applyAutomation(result.automation, result.triggers.data as AutomationTriggerRecord[]);
        this.loading.set(false);
        this.refreshValidation();
      });
  }

  selectNode(nodeId: string): void {
    this.selectedNodeId.set(nodeId);
  }

  selectNodeFromKeyboard(event: Event, nodeId: string): void {
    event.preventDefault();
    this.selectNode(nodeId);
  }

  createConnection(event: FCreateConnectionEvent): void {
    this.upsertConnection(event.sourceId, this.resolveDropInput(event.targetId, event.dropPosition));
  }

  reassignConnection(event: FReassignConnectionEvent): void {
    const sourceId = event.nextSourceId ?? event.previousSourceId;
    const targetId = this.resolveDropInput(event.nextTargetId ?? event.previousTargetId, event.dropPosition);

    if (!sourceId || !targetId) {
      this.removeConnection(event.connectionId);
      return;
    }

    this.upsertConnection(sourceId, targetId, event.connectionId);
  }

  removeConnection(connectionId: string): void {
    const previousLength = this.connections().length;
    this.connections.update((connections) => connections.filter((connection) => connection.id !== connectionId));
    if (this.connections().length !== previousLength) this.markDirty();
  }

  toggleBlockMenu(): void {
    this.blockMenuOpen.update((value) => !value);
  }

  closeBlockMenu(): void {
    this.blockMenuOpen.set(false);
  }

  selectTemplateGroup(group: AutomationTemplateGroup): void {
    this.selectedTemplateGroup.set(group);
    this.librarySearch.set('');
  }

  templateGroupCount(group: AutomationTemplateGroup): number {
    return this.templates.filter((template) => templateGroup(template) === group).length;
  }

  isNodeExpanded(nodeId: string): boolean {
    return this.expandedNodeIds().includes(nodeId);
  }

  toggleNodeExpanded(nodeId: string, event?: Event): void {
    event?.stopPropagation();
    this.selectedNodeId.set(nodeId);
    this.expandedNodeIds.update((nodeIds) =>
      nodeIds.includes(nodeId) ? nodeIds.filter((currentId) => currentId !== nodeId) : [...nodeIds, nodeId],
    );
  }

  addTemplate(template: AutomationTemplate): void {
    if (template.kind === 'trigger') {
      this.nodes.update((nodes) => nodes.map((node) =>
        node.kind === 'trigger'
          ? this.mergeTriggerTemplate(node, template)
          : node,
      ));
      this.selectedNodeId.set(this.nodes().find((node) => node.kind === 'trigger')?.id ?? this.selectedNodeId());
      this.blockMenuOpen.set(false);
      this.markDirty();
      return;
    }

    const previousNode = this.selectedNode() ?? this.nodes().at(-1) ?? null;
    const id = `${template.templateId.replace(/\./g, '-')}-${Date.now()}`;
    const newNode = createNodeFromTemplate(template, {
      id,
      stepKey: uniqueStepKey(template.title, this.nodes()),
      position: this.newNodePosition(previousNode, this.nodes().length),
      inputId: `${id}-in`,
      outputId: template.kind === 'end' ? undefined : `${id}-out`,
    });

    this.nodes.update((nodes) => [...nodes, newNode]);
    if (previousNode?.outputId && newNode.inputId) {
      this.upsertConnection(previousNode.outputId, newNode.inputId, `c-${previousNode.id}-${newNode.id}`, { silent: true });
    }
    this.selectedNodeId.set(id);
    this.expandedNodeIds.set([id]);
    this.blockMenuOpen.set(false);
    this.markDirty();
  }

  deleteNode(nodeId: string, event?: Event): void {
    event?.stopPropagation();
    const node = this.nodes().find((item) => item.id === nodeId);
    if (!node) return;
    if (node.kind === 'trigger' && this.nodes().filter((item) => item.kind === 'trigger').length === 1) {
      this.toast.info('Mantenha um gatilho para definir como a automacao inicia.');
      return;
    }

    this.nodes.update((nodes) => nodes.filter((item) => item.id !== nodeId));
    this.connections.update((connections) =>
      connections.filter((connection) => connection.from !== node.outputId && connection.to !== node.inputId),
    );
    if (this.selectedNodeId() === nodeId) {
      this.selectedNodeId.set(this.nodes()[0]?.id ?? '');
    }
    this.expandedNodeIds.update((ids) => ids.filter((id) => id !== nodeId));
    this.markDirty();
  }

  moveNode(nodeId: string, position: FlowPoint): void {
    this.nodes.update((nodes) =>
      nodes.map((node) => (node.id === nodeId ? { ...node, position: { x: position.x, y: position.y } } : node)),
    );
    this.markDirty();
  }

  updateField(nodeId: string, fieldKey: string, event: Event): void {
    this.setFieldValue(nodeId, fieldKey, readEventValue(event));
  }

  updateBooleanField(nodeId: string, fieldKey: string, value: boolean): void {
    this.setFieldValue(nodeId, fieldKey, String(value));
  }

  setLibrarySearch(event: Event): void {
    this.librarySearch.set(readEventValue(event));
  }

  connectableInputIds(node: AutomationFlowNode): string[] {
    if (!node.outputId || node.kind === 'end') return [];
    return this.nodes()
      .filter((candidate) => candidate.inputId && !this.connectionValidationIssue(node, candidate))
      .map((candidate) => candidate.inputId ?? '');
  }

  incomingForNode(node: AutomationFlowNode): AutomationConnection | null {
    if (!node.inputId) return null;
    return this.connections().find((connection) => connection.to === node.inputId) ?? null;
  }

  outgoingForNode(node: AutomationFlowNode): readonly AutomationConnection[] {
    if (!node.outputId) return [];
    return this.connections().filter((connection) => connection.from === node.outputId);
  }

  disconnectNodeInput(node: AutomationFlowNode, event: Event): void {
    event.stopPropagation();
    const incoming = this.incomingForNode(node);
    if (incoming) this.removeConnection(incoming.id);
  }

  nodeTitleByInput(inputId: string): string {
    return this.nodeByInput(inputId)?.title ?? 'Bloco removido';
  }

  nodeTitleByOutput(outputId: string): string {
    return this.nodeByOutput(outputId)?.title ?? 'Bloco removido';
  }

  visibleNodeFields(node: AutomationFlowNode): readonly NodeField[] {
    const mode = this.fieldValue(node, 'mode');
    const triggerType = this.fieldValue(node, 'triggerType');
    return node.settings.filter((field) => {
      if (node.kind === 'condition') {
        if (field.key === 'llmPrompt') return mode === 'llm';
        if (['field', 'operator', 'value'].includes(field.key)) return mode !== 'llm';
      }
      if (node.kind === 'trigger' && field.key === 'assignmentKey') {
        return triggerType === 'conversation_assignment';
      }
      return true;
    });
  }

  fieldOptions(node: AutomationFlowNode, field: NodeField): readonly SelectOption[] {
    switch (field.optionSource) {
      case 'businessFields':
        return this.businessFields().map((item) => ({
          value: item.value,
          label: `${FIELD_GROUP_LABELS[item.group]}: ${item.label}`,
          description: item.description,
        }));
      case 'channelAccounts':
        return this.channelAccounts().map((account) => ({
          value: account.id,
          label: account.label || account.externalSenderId || account.externalChannelId || account.id,
          description: account.kind,
        }));
      case 'conditionModes':
        return CONDITION_MODE_OPTIONS;
      case 'conditionOperators':
        return CONDITION_OPERATORS[this.selectedConditionField(node)?.type ?? 'text'];
      case 'conditionValues': {
        const selectedField = this.selectedConditionField(node);
        if (selectedField?.type === 'boolean') return BOOLEAN_VALUE_OPTIONS;
        return selectedField?.options ?? [];
      }
      case 'tools':
        return this.tools().map((tool) => ({
          value: tool.id,
          label: tool.displayName || tool.key,
          description: `${tool.owningDomain} - risco ${tool.riskLevel}`,
        }));
      case 'triggerTypes':
        return TRIGGER_TYPE_OPTIONS;
      case 'handoffTargets':
        return HANDOFF_TARGET_OPTIONS;
      default:
        return [];
    }
  }

  fieldControlId(nodeId: string, fieldKey: string): string {
    return `automation-field-${nodeId}-${fieldKey}`;
  }

  fieldOptionsId(nodeId: string, fieldKey: string): string {
    return `automation-options-${nodeId}-${fieldKey}`;
  }

  fieldInputType(field: NodeField): string {
    return field.type === 'number' ? 'number' : 'text';
  }

  fieldDisplayValue(node: AutomationFlowNode, field: NodeField): string {
    const value = field.value;
    if (!value) return 'Nao definido';
    return this.fieldOptions(node, field).find((option) => option.value === value)?.label ?? value;
  }

  previewField(node: AutomationFlowNode): NodeField | null {
    return this.visibleNodeFields(node).find((field) => field.value.trim()) ?? this.visibleNodeFields(node)[0] ?? null;
  }

  outputFieldsForNode(node: AutomationFlowNode): string[] {
    if (node.kind === 'trigger') {
      return uniqueStrings([...node.outputFields, ...BASE_BUSINESS_FIELDS.map((field) => field.value)]);
    }
    if (node.stepType === 'agent_run' && this.fieldValue(node, 'fields')) {
      return uniqueStrings([
        ...node.outputFields,
        ...this.fieldValue(node, 'fields')
          .split(',')
          .map((field) => field.trim())
          .filter(Boolean)
          .map((field) => `lead.${slugField(field)}`),
      ]);
    }
    if (node.stepType === 'integration_action') {
      const prefix = this.fieldValue(node, 'outputPrefix') || 'tool.result';
      const tool = this.tools().find((item) => item.id === this.fieldValue(node, 'toolId'));
      const schemaFields = schemaPropertyKeys(tool?.outputSchema).map((field) => `${prefix}.${field}`);
      return uniqueStrings(schemaFields.length ? schemaFields : node.outputFields);
    }
    return uniqueStrings([...node.outputFields]);
  }

  requiredInputFields(node: AutomationFlowNode): string[] {
    if (node.kind === 'trigger') return [];
    const fields = new Set<string>();
    if (node.kind === 'condition') {
      if (this.fieldValue(node, 'mode') === 'llm') {
        for (const token of fieldTokens(this.fieldValue(node, 'llmPrompt'))) fields.add(token);
        if (fields.size === 0) fields.add('message.text');
      } else {
        const field = this.fieldValue(node, 'field');
        if (field) fields.add(field);
      }
    }
    if (node.stepType === 'integration_action') {
      const inputField = this.fieldValue(node, 'toolInput');
      if (inputField) fields.add(inputField);
    }
    for (const field of node.settings) {
      for (const token of fieldTokens(field.value)) fields.add(token);
    }
    return [...fields].filter((field) => this.businessFields().some((item) => item.value === field));
  }

  selectedChannelAccount(): ChannelAccount | null {
    const trigger = this.nodes().find((node) => node.kind === 'trigger');
    const channelId = trigger ? this.fieldValue(trigger, 'channelAccountId') : '';
    return this.channelAccounts().find((account) => account.id === channelId) ?? null;
  }

  saveDraft(): void {
    this.persist('draft');
  }

  activate(): void {
    this.persist('active');
  }

  runTest(): void {
    const automationId = this.automationId();
    if (!automationId) {
      this.toast.info('Salve o rascunho antes de executar um teste.');
      return;
    }

    this.runningTest.set(true);
    this.api
      .createAutomationRun({
        automationId,
        triggerType: 'manual',
        input: {
          source: 'automation_builder',
          previewFields: this.businessFields().slice(0, 12).map((field) => field.value),
        },
        contextRefs: [],
      })
      .pipe(
        catchError(() => {
          this.toast.error('Nao foi possivel enfileirar o teste da automacao.');
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((run) => {
        this.runningTest.set(false);
        if (run) this.toast.success('Teste da automacao enfileirado.');
      });
  }

  badgeTone(badge: string): 'success' | 'warning' | 'danger' | 'info' | 'neutral' | 'accent' {
    const normalized = normalize(badge);
    if (normalized.includes('sensivel')) return 'danger';
    if (normalized.includes('sistema')) return 'accent';
    if (normalized.includes('ia') || normalized.includes('llm')) return 'info';
    if (normalized.includes('assignment')) return 'warning';
    return 'neutral';
  }

  kindClass(kind: AutomationNodeKind): string {
    return `flow-node--${kind}`;
  }

  issueForNode(node: AutomationFlowNode): WorkflowIssue | null {
    return this.validationIssues().find((issue) => issue.nodeId === node.id && issue.tone === 'danger') ?? null;
  }

  private applyAutomation(automation: AutomationRecord, triggers: readonly AutomationTriggerRecord[]): void {
    this.automationId.set(automation.id);
    this.automationName.set(automation.name || AUTOMATION_NAME);
    this.status.set(asAutomationStatus(automation.status));
    const trigger = triggers.find((item) => asRecord(item.triggerPolicy)['source'] === 'automation_builder') ?? triggers[0] ?? null;
    if (trigger) this.automationTriggerId.set(trigger.id);

    const parsed = this.parseBuilderAutomation(automation, trigger);
    if (parsed) {
      this.nodes.set(parsed.nodes);
      this.connections.set(parsed.connections);
      this.selectedNodeId.set(parsed.nodes.find((node) => node.kind !== 'trigger')?.id ?? parsed.nodes[0]?.id ?? '');
    }
  }

  private parseBuilderAutomation(
    automation: AutomationRecord,
    trigger: AutomationTriggerRecord | null,
  ): { nodes: AutomationFlowNode[]; connections: AutomationConnection[] } | null {
    const steps = Array.isArray(automation.steps) ? automation.steps : [];
    const parsedSteps = steps
      .map((step) => {
        const config = asRecord(step.config);
        const builder = asRecord(config['builder']);
        return Object.keys(builder).length ? { step, builder } : null;
      })
      .filter((item): item is { step: { key: string; stepType: AutomationStepStepTypeEnumKey; config: JsonObject }; builder: JsonObject } => Boolean(item));

    if (parsedSteps.length === 0) return null;

    const triggerBuilder = asRecord(asRecord(automation.triggerDefaultsPolicy)['builder']);
    const triggerNode = createNodeFromTemplate(TRIGGER_NODE_TEMPLATE, {
      id: stringValue(triggerBuilder['nodeId']) || 'trigger-conversation',
      stepKey: 'trigger',
      position: pointValue(triggerBuilder['position']) ?? { x: 40, y: 150 },
      outputId: stringValue(triggerBuilder['outputId']) || 'trigger-conversation-out',
      settings: restoreSettings(TRIGGER_NODE_TEMPLATE.settings, asRecord(triggerBuilder['settings'])),
    });
    triggerNode.settings = triggerNode.settings.map((field) => {
      if (field.key === 'channelAccountId') {
        return { ...field, value: trigger?.channelAccountId ?? stringValue(triggerBuilder['channelAccountId']) };
      }
      if (field.key === 'triggerType') {
        return { ...field, value: (trigger?.triggerType ?? stringValue(triggerBuilder['triggerType'])) || 'conversation_assignment' };
      }
      return field;
    });

    const nodes = [
      triggerNode,
      ...parsedSteps.map(({ step, builder }) => this.nodeFromBuilderStep(step, builder)),
    ];
    const byStepKey = new Map(nodes.map((node) => [node.stepKey, node]));
    const explicitTriggerConnections = Array.isArray(triggerBuilder['routes'])
      ? triggerBuilder['routes']
          .map((route) => asRecord(route))
          .flatMap((route) => {
            const target = byStepKey.get(stringValue(route['targetStepKey']));
            if (!triggerNode.outputId || !target?.inputId) return [];
            return [{
              id: `c-${triggerNode.id}-${target.id}-${stringValue(route['branchKey']) || 'start'}`,
              from: triggerNode.outputId,
              to: target.inputId,
              branchKey: stringValue(route['branchKey']) || null,
            }];
          })
      : [];
    const stepConnections = parsedSteps.flatMap(({ step, builder }) => {
      const source = byStepKey.get(step.key);
      if (!source?.outputId || !Array.isArray(builder['routes'])) return [];
      return builder['routes']
        .map((route) => asRecord(route))
        .flatMap((route) => {
          const target = byStepKey.get(stringValue(route['targetStepKey']));
          if (!target?.inputId) return [];
          return [{
            id: `c-${source.id}-${target.id}-${stringValue(route['branchKey']) || 'next'}`,
            from: source.outputId!,
            to: target.inputId,
            branchKey: stringValue(route['branchKey']) || null,
          }];
        });
    });
    const targetInputIds = new Set(stepConnections.map((connection) => connection.to));
    const inferredTriggerConnections = explicitTriggerConnections.length > 0 || !triggerNode.outputId
      ? []
      : parsedSteps.flatMap(({ step }) => {
          const target = byStepKey.get(step.key);
          if (!target?.inputId || targetInputIds.has(target.inputId)) return [];
          return [{
            id: `c-${triggerNode.id}-${target.id}-start`,
            from: triggerNode.outputId!,
            to: target.inputId,
            branchKey: null,
          }];
        });

    return { nodes, connections: [...explicitTriggerConnections, ...inferredTriggerConnections, ...stepConnections] };
  }

  private nodeFromBuilderStep(
    step: { key: string; stepType: AutomationStepStepTypeEnumKey; config: JsonObject },
    builder: JsonObject,
  ): AutomationFlowNode {
    const fallbackTemplate = templateForStepType(step.stepType);
    const nodeId = stringValue(builder['nodeId']) || `node-${step.key}`;
    return {
      ...createNodeFromTemplate(fallbackTemplate, {
        id: nodeId,
        stepKey: step.key,
        position: pointValue(builder['position']) ?? { x: 360, y: 160 },
        inputId: stringValue(builder['inputId']) || `${nodeId}-in`,
        outputId: step.stepType === 'end' ? undefined : stringValue(builder['outputId']) || `${nodeId}-out`,
      }),
      kind: asNodeKind(stringValue(builder['kind'])) ?? fallbackTemplate.kind,
      icon: stringValue(builder['icon']) || fallbackTemplate.icon,
      title: stringValue(builder['title']) || fallbackTemplate.title,
      eyebrow: stringValue(builder['eyebrow']) || fallbackTemplate.eyebrow,
      description: stringValue(builder['description']) || fallbackTemplate.description,
      badges: stringArray(builder['badges']).length ? stringArray(builder['badges']) : fallbackTemplate.badges,
      settings: restoreSettings(fallbackTemplate.settings, asRecord(builder['settings'])),
      outputFields: stringArray(builder['outputFields']).length ? stringArray(builder['outputFields']) : fallbackTemplate.outputFields,
      policies: stringArray(builder['policies']).length ? stringArray(builder['policies']) : fallbackTemplate.policies,
    };
  }

  private persist(status: AutomationStatus): void {
    const issues = this.validateWorkflow(status);
    this.validationIssues.set(issues);
    if (issues.some((issue) => issue.tone === 'danger')) {
      this.toast.error('Corrija as pendencias do workflow antes de salvar.');
      return;
    }

    this.saving.set(true);
    const payload = this.buildAutomationPayload(status);
    const request = this.automationId()
      ? this.api.updateAutomation(this.automationId()!, payload)
      : this.api.createAutomation(payload);

    request
      .pipe(
        switchMap((automation) => this.syncTrigger(automation as AutomationRecord, status).pipe(map(() => automation))),
        catchError(() => {
          this.toast.error('Nao foi possivel salvar a automacao.');
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((automation) => {
        this.saving.set(false);
        if (!automation) return;
        this.automationId.set(automation.id);
        this.status.set(status);
        this.dirty.set(false);
        this.toast.success(status === 'active' ? 'Automacao ativada.' : 'Rascunho salvo.');
        this.loadWorkspace();
      });
  }

  private syncTrigger(automation: AutomationRecord, status: AutomationStatus): Observable<unknown> {
    const triggerNode = this.nodes().find((node) => node.kind === 'trigger');
    if (!triggerNode) return of(null);
    const channelAccountId = this.fieldValue(triggerNode, 'channelAccountId') || null;
    if (!channelAccountId) return of(null);

    const triggerType = asTriggerType(this.fieldValue(triggerNode, 'triggerType')) ?? 'conversation_assignment';
    const body: AutomationTriggerInput = {
      name: triggerNode.title,
      status: status === 'active' ? 'active' : 'paused',
      triggerType,
      channelAccountId,
      triggerPolicy: {
        source: 'automation_builder',
        triggerNodeId: triggerNode.id,
        assignmentKey: this.fieldValue(triggerNode, 'assignmentKey'),
        filter: this.fieldValue(triggerNode, 'filter'),
        outputFields: this.outputFieldsForNode(triggerNode),
      },
      conversationContextPolicy: {
        availableFields: BASE_BUSINESS_FIELDS.map((field) => field.value),
      },
    };

    const currentTriggerId = this.automationTriggerId()
      ?? this.automationTriggers().find((trigger) => asRecord(trigger.triggerPolicy)['source'] === 'automation_builder')?.id
      ?? null;
    if (currentTriggerId) {
      return this.api.updateAutomationTrigger(automation.id, currentTriggerId, body).pipe(map((trigger) => {
        this.automationTriggerId.set(trigger.id);
        return trigger;
      }));
    }
    return this.api.createAutomationTrigger(automation.id, body).pipe(map((trigger) => {
      this.automationTriggerId.set(trigger.id);
      return trigger;
    }));
  }

  private buildAutomationPayload(status: AutomationStatus): AutomationDefinitionInput {
    const triggerNode = this.nodes().find((node) => node.kind === 'trigger');
    const steps = this.nodes()
      .filter((node) => node.kind !== 'trigger')
      .map((node) => this.nodeToStep(node));

    return {
      key: AUTOMATION_KEY,
      name: this.automationName().trim() || AUTOMATION_NAME,
      description: 'Workflow deterministico configurado pelo Agent Builder.',
      status,
      steps,
      triggerDefaultsPolicy: {
        builder: {
          nodeId: triggerNode?.id,
          outputId: triggerNode?.outputId,
          position: triggerNode?.position,
          settings: triggerNode ? settingsRecord(triggerNode.settings) : {},
          channelAccountId: triggerNode ? this.fieldValue(triggerNode, 'channelAccountId') : null,
          triggerType: triggerNode ? this.fieldValue(triggerNode, 'triggerType') : null,
          outputFields: triggerNode ? this.outputFieldsForNode(triggerNode) : [],
          routes: triggerNode ? this.builderRoutesForNode(triggerNode) : [],
          availableFields: BASE_BUSINESS_FIELDS.map((field) => field.value),
        },
      },
      defaultContextPolicy: {
        fieldCatalog: this.businessFields().map((field) => ({
          key: field.value,
          label: field.label,
          group: field.group,
          type: field.type,
        })),
      },
      errorPolicy: {
        onStepFailure: 'stop_and_request_handoff',
        audit: true,
      },
      rateLimitPolicy: {
        perContactWindow: 'conversation',
      },
    };
  }

  private nodeToStep(node: AutomationFlowNode): AutomationDefinitionInput['steps'][number] {
    const outputFields = this.outputFieldsForNode(node);
    const inputFields = this.requiredInputFields(node);
    const routes = this.builderRoutesForNode(node);
    const stepType = this.stepTypeForNode(node);
    const settings = settingsRecord(node.settings);
    const config: JsonObject = {
      ...settings,
      builder: {
        nodeId: node.id,
        kind: node.kind,
        icon: node.icon,
        title: node.title,
        eyebrow: node.eyebrow,
        description: node.description,
        position: node.position,
        inputId: node.inputId,
        outputId: node.outputId,
        badges: node.badges,
        settings,
        inputFields,
        outputFields,
        policies: node.policies,
        routes,
      },
    };

    if (stepType === 'ai_condition') {
      config['prompt'] = this.fieldValue(node, 'llmPrompt');
      config['inputPolicy'] = { fields: inputFields };
      config['outputSchema'] = {
        type: 'object',
        properties: {
          route: { type: 'string' },
          reason: { type: 'string' },
        },
        required: ['route'],
      };
      config['branchMapping'] = Object.fromEntries(routes.map((route) => [route.branchKey ?? route.targetStepKey, route.targetStepKey]));
    }

    if (stepType === 'condition') {
      config['condition'] = {
        field: this.fieldValue(node, 'field'),
        operator: this.fieldValue(node, 'operator'),
        value: this.fieldValue(node, 'value'),
        fieldType: this.selectedConditionField(node)?.type ?? 'text',
      };
    }

    return {
      key: node.stepKey,
      stepType,
      config,
      ...(node.stepType === 'wait' ? { loopPolicy: { maxDurationSeconds: Number(this.fieldValue(node, 'timeout') || 30) * 60 } } : {}),
    };
  }

  private builderRoutesForNode(node: AutomationFlowNode): { targetStepKey: string; branchKey: string | null }[] {
    return this.outgoingForNode(node).flatMap((connection) => {
      const target = this.nodeByInput(connection.to);
      return target ? [{ targetStepKey: target.stepKey, branchKey: connection.branchKey ?? null }] : [];
    });
  }

  private validateWorkflow(status: AutomationStatus): WorkflowIssue[] {
    const issues: WorkflowIssue[] = [];
    const nodes = this.nodes();
    const triggerNode = nodes.find((node) => node.kind === 'trigger');
    if (!triggerNode) {
      issues.push({ tone: 'danger', message: 'Adicione um gatilho para iniciar a automacao.' });
    } else if (status === 'active' && !this.fieldValue(triggerNode, 'channelAccountId')) {
      issues.push({ nodeId: triggerNode.id, tone: 'danger', message: 'Selecione o canal do gatilho antes de ativar.' });
    }

    for (const connection of this.connections()) {
      const source = this.nodeByOutput(connection.from);
      const target = this.nodeByInput(connection.to);
      const message = this.connectionValidationIssue(source, target);
      if (message) issues.push({ nodeId: target?.id ?? source?.id, tone: 'danger', message });
    }

    for (const node of nodes.filter((item) => item.kind !== 'trigger')) {
      if (!this.incomingForNode(node)) {
        issues.push({ nodeId: node.id, tone: status === 'active' ? 'danger' : 'warning', message: `${node.title} esta sem entrada.` });
      }
      const missing = this.requiredInputFields(node).filter((field) => !this.fieldsAvailableBeforeNode(node).has(field));
      if (missing.length > 0) {
        issues.push({
          nodeId: node.id,
          tone: 'danger',
          message: `${node.title} precisa de campos que nao chegam pela cadeia: ${missing.join(', ')}.`,
        });
      }
    }

    if (!nodes.some((node) => node.kind === 'end')) {
      issues.push({ tone: status === 'active' ? 'danger' : 'warning', message: 'Inclua um bloco terminal para encerrar o fluxo.' });
    }

    const cycleNodeId = this.detectCycle();
    if (cycleNodeId) {
      issues.push({ nodeId: cycleNodeId, tone: 'danger', message: 'O workflow nao pode conter ciclos.' });
    }

    return issues;
  }

  private refreshValidation(): void {
    this.validationIssues.set(this.validateWorkflow(this.status()));
  }

  private upsertConnection(
    sourceId: string | undefined,
    targetId: string | undefined,
    connectionId = `c-${Date.now()}`,
    options?: { silent?: boolean },
  ): void {
    const sourceNode = sourceId ? this.nodeByOutput(sourceId) : null;
    const targetNode = targetId ? this.nodeByInput(targetId) : null;
    const issue = this.connectionValidationIssue(sourceNode, targetNode);
    if (issue) {
      if (!options?.silent) this.toast.info(issue);
      return;
    }
    if (!sourceId || !targetId || !sourceNode || !targetNode) return;

    this.connections.update((connections) => {
      const nextConnections = connections.filter(
        (connection) => connection.id !== connectionId && connection.to !== targetId,
      );
      return [...nextConnections, { id: connectionId, from: sourceId, to: targetId }];
    });
    this.markDirty();
  }

  private connectionValidationIssue(
    sourceNode: AutomationFlowNode | null,
    targetNode: AutomationFlowNode | null,
  ): string | null {
    if (!sourceNode || !targetNode) return 'Solte a ligacao em uma entrada valida.';
    if (!sourceNode.outputId || !targetNode.inputId) return 'A ligacao precisa sair de uma saida e chegar em uma entrada.';
    if (sourceNode.id === targetNode.id) return 'Um bloco nao pode ligar nele mesmo.';
    if (targetNode.kind === 'trigger') return 'Gatilhos so iniciam o fluxo; eles nao recebem entrada.';
    if (sourceNode.kind === 'end') return 'Blocos terminais nao podem ter saida.';
    if (this.wouldCreateCycle(sourceNode.id, targetNode.id)) return 'Essa conexao criaria um ciclo no workflow.';

    const available = this.collectAvailableFieldsThrough(sourceNode.id);
    const missing = this.requiredInputFields(targetNode).filter((field) => !available.has(field));
    if (missing.length > 0) {
      return `A cadeia anterior nao produz: ${missing.join(', ')}.`;
    }
    return null;
  }

  private collectAvailableFieldsThrough(nodeId: string): Set<string> {
    const fields = new Set(BASE_BUSINESS_FIELDS.map((field) => field.value));
    const visit = (currentId: string, visited = new Set<string>()): void => {
      if (visited.has(currentId)) return;
      visited.add(currentId);
      const current = this.nodes().find((node) => node.id === currentId);
      if (!current) return;
      for (const field of this.outputFieldsForNode(current)) fields.add(field);
      const incoming = this.incomingForNode(current);
      const previous = incoming ? this.nodeByOutput(incoming.from) : null;
      if (previous) visit(previous.id, visited);
    };
    visit(nodeId);
    return fields;
  }

  private fieldsAvailableBeforeNode(node: AutomationFlowNode): Set<string> {
    const incoming = this.incomingForNode(node);
    const previous = incoming ? this.nodeByOutput(incoming.from) : null;
    return previous ? this.collectAvailableFieldsThrough(previous.id) : new Set(BASE_BUSINESS_FIELDS.map((field) => field.value));
  }

  private detectCycle(): string | null {
    const visiting = new Set<string>();
    const visited = new Set<string>();

    const visit = (nodeId: string): string | null => {
      if (visiting.has(nodeId)) return nodeId;
      if (visited.has(nodeId)) return null;
      visiting.add(nodeId);
      const node = this.nodes().find((item) => item.id === nodeId);
      for (const connection of node ? this.outgoingForNode(node) : []) {
        const target = this.nodeByInput(connection.to);
        if (!target) continue;
        const cycle = visit(target.id);
        if (cycle) return cycle;
      }
      visiting.delete(nodeId);
      visited.add(nodeId);
      return null;
    };

    for (const node of this.nodes()) {
      const cycle = visit(node.id);
      if (cycle) return cycle;
    }
    return null;
  }

  private wouldCreateCycle(sourceNodeId: string, targetNodeId: string): boolean {
    const visit = (nodeId: string, visited = new Set<string>()): boolean => {
      if (nodeId === sourceNodeId) return true;
      if (visited.has(nodeId)) return false;
      visited.add(nodeId);
      const node = this.nodes().find((item) => item.id === nodeId);
      if (!node) return false;
      return this.outgoingForNode(node).some((connection) => {
        const target = this.nodeByInput(connection.to);
        return target ? visit(target.id, visited) : false;
      });
    };
    return visit(targetNodeId);
  }

  private setFieldValue(nodeId: string, fieldKey: string, value: string): void {
    const businessFields = this.businessFields();
    this.nodes.update((nodes) =>
      nodes.map((node) => {
        if (node.id !== nodeId) return node;
        const settings = node.settings.map((field) => (field.key === fieldKey ? { ...field, value } : field));
        return {
          ...node,
          settings: node.kind === 'condition'
            ? normalizeConditionSettings(settings, businessFields, fieldKey)
            : settings,
        };
      }),
    );
    this.markDirty();
  }

  private selectedConditionField(node: AutomationFlowNode): BusinessField | null {
    const value = this.fieldValue(node, 'field');
    return this.businessFields().find((field) => field.value === value) ?? null;
  }

  private fieldValue(node: AutomationFlowNode, key: string): string {
    return node.settings.find((field) => field.key === key)?.value ?? '';
  }

  private stepTypeForNode(node: AutomationFlowNode): AutomationStepStepTypeEnumKey {
    if (node.kind === 'condition' && this.fieldValue(node, 'mode') === 'llm') return 'ai_condition';
    if (node.kind === 'condition') return 'condition';
    return node.stepType === 'trigger' ? 'agent_run' : node.stepType;
  }

  private markDirty(): void {
    this.dirty.set(true);
    this.refreshValidation();
  }

  private nodeByInput(inputId: string): AutomationFlowNode | null {
    return this.nodes().find((node) => node.inputId === inputId) ?? null;
  }

  private nodeByOutput(outputId: string): AutomationFlowNode | null {
    return this.nodes().find((node) => node.outputId === outputId) ?? null;
  }

  private newNodePosition(previousNode: AutomationFlowNode | null, index: number): FlowPoint {
    if (!previousNode) return { x: 120 + (index % 3) * 320, y: 120 + Math.floor(index / 3) * 220 };
    return { x: previousNode.position.x + 320, y: previousNode.position.y + (index % 2 === 0 ? 120 : -80) };
  }

  private resolveDropInput(targetId: string | undefined, dropPosition?: FlowPoint): string | undefined {
    if (targetId) return targetId;
    if (!dropPosition) return undefined;

    const inputPorts = Array.from(document.querySelectorAll<HTMLElement>('.automation-flow .flow-port--in[data-port-id]'));
    const nearest = inputPorts
      .map((port) => {
        const rect = port.getBoundingClientRect();
        const centerX = rect.left + rect.width / 2;
        const centerY = rect.top + rect.height / 2;
        return { id: port.dataset['portId'], distance: Math.hypot(centerX - dropPosition.x, centerY - dropPosition.y) };
      })
      .filter((candidate): candidate is { id: string; distance: number } => Boolean(candidate.id))
      .sort((first, second) => first.distance - second.distance)[0];

    return nearest && nearest.distance <= 56 ? nearest.id : undefined;
  }

  private mergeTriggerTemplate(current: AutomationFlowNode, template: AutomationTemplate): AutomationFlowNode {
    const next = createNodeFromTemplate(template, {
      id: current.id,
      stepKey: current.stepKey,
      position: current.position,
      outputId: current.outputId,
    });
    return {
      ...next,
      settings: next.settings.map((field) => {
        const currentField = current.settings.find((item) => item.key === field.key);
        return currentField ? { ...field, value: currentField.value } : field;
      }),
    };
  }
}

function createNodeFromTemplate(
  template: AutomationTemplate,
  overrides: Partial<AutomationFlowNode> & { id: string; stepKey: string; position: FlowPoint },
): AutomationFlowNode {
  return {
    id: overrides.id,
    stepKey: overrides.stepKey,
    kind: overrides.kind ?? template.kind,
    icon: overrides.icon ?? template.icon,
    title: overrides.title ?? template.title,
    eyebrow: overrides.eyebrow ?? template.eyebrow,
    description: overrides.description ?? template.description,
    stepType: overrides.stepType ?? template.stepType,
    position: overrides.position,
    inputId: overrides.inputId,
    outputId: overrides.outputId,
    badges: overrides.badges ?? [...template.badges],
    settings: overrides.settings ?? template.settings.map((field) => ({ ...field })),
    outputFields: overrides.outputFields ?? [...template.outputFields],
    policies: overrides.policies ?? [...template.policies],
  };
}

function templateGroup(template: AutomationTemplate): AutomationTemplateGroup {
  if (template.kind === 'trigger') return 'trigger';
  if (template.kind === 'condition') return 'condition';
  if (template.library === 'Integracao') return 'tool';
  if (template.library === 'Sistema') return 'system';
  return 'flow';
}

function templateForStepType(stepType: AutomationStepStepTypeEnumKey): AutomationTemplate {
  if (stepType === 'condition' || stepType === 'ai_condition') return AUTOMATION_TEMPLATES[1];
  if (stepType === 'integration_action' || stepType === 'webhook_call' || stepType === 'update_crm') return AUTOMATION_TEMPLATES[3];
  if (stepType === 'wait') return AUTOMATION_TEMPLATES[4];
  if (stepType === 'agent_run') return AUTOMATION_TEMPLATES[5];
  if (stepType === 'handoff') return AUTOMATION_TEMPLATES[6];
  if (stepType === 'end') return AUTOMATION_TEMPLATES[9];
  return AUTOMATION_TEMPLATES[5];
}

function restoreSettings(defaults: readonly NodeField[], values: JsonObject): NodeField[] {
  return defaults.map((field) => ({
    ...field,
    value: restoredFieldValue(field, values[field.key]),
  }));
}

function restoredFieldValue(field: NodeField, saved: unknown): string {
  if (typeof saved !== 'string') return field.value;
  if (saved.trim() || !field.value) return saved;
  return field.value;
}

function normalizeConditionSettings(
  settings: readonly NodeField[],
  businessFields: readonly BusinessField[],
  changedKey: string,
): NodeField[] {
  if (changedKey !== 'field') return [...settings];

  const selectedFieldKey = settings.find((field) => field.key === 'field')?.value ?? '';
  const selectedField = businessFields.find((field) => field.value === selectedFieldKey);
  const operators = CONDITION_OPERATORS[selectedField?.type ?? 'text'];
  const currentOperator = settings.find((field) => field.key === 'operator')?.value ?? '';
  const nextOperator = operators.some((operator) => operator.value === currentOperator)
    ? currentOperator
    : operators[0]?.value ?? '';
  const valueOptions = selectedField?.type === 'boolean' ? BOOLEAN_VALUE_OPTIONS : selectedField?.options ?? [];
  const currentValue = settings.find((field) => field.key === 'value')?.value ?? '';
  const clearValue = valueOptions.length > 0
    && currentValue
    && !valueOptions.some((option) => option.value === currentValue);

  return settings.map((field) => {
    if (field.key === 'operator') return { ...field, value: nextOperator };
    if (field.key === 'value' && clearValue) return { ...field, value: '' };
    return { ...field };
  });
}

function settingsRecord(fields: readonly NodeField[]): JsonObject {
  return Object.fromEntries(fields.map((field) => [field.key, field.value]));
}

function readEventValue(event: Event): string {
  const target = event.target;
  return target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement || target instanceof HTMLSelectElement
    ? target.value
    : '';
}

function normalize(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
}

function uniqueStepKey(title: string, nodes: readonly AutomationFlowNode[]): string {
  const base = slugField(title) || 'bloco';
  const used = new Set(nodes.map((node) => node.stepKey));
  let candidate = base;
  let index = 2;
  while (used.has(candidate)) {
    candidate = `${base}-${index}`;
    index += 1;
  }
  return candidate;
}

function slugField(value: string): string {
  return normalize(value)
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

function uniqueStrings(values: readonly string[]): string[] {
  return [...new Set(values.filter(Boolean))];
}

function fieldTokens(value: string): string[] {
  return [...value.matchAll(/\{\{\s*([a-zA-Z0-9_.-]+)\s*\}\}/g)].map((match) => match[1]);
}

function schemaPropertyKeys(schema: unknown): string[] {
  const properties = asRecord(asRecord(schema)['properties']);
  return Object.keys(properties);
}

function humanizeFieldKey(fieldKey: string): string {
  return fieldKey
    .split('.')
    .at(-1)!
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toLocaleUpperCase('pt-BR'));
}

function inferBusinessFieldType(fieldKey: string): BusinessFieldType {
  if (fieldKey.includes('score') || fieldKey.includes('amount') || fieldKey.includes('count')) return 'number';
  if (fieldKey.includes('blocked') || fieldKey.includes('open') || fieldKey.startsWith('is_')) return 'boolean';
  if (fieldKey.includes('at') || fieldKey.includes('date')) return 'date';
  if (fieldKey.includes('fields') || fieldKey.includes('body')) return 'json';
  return 'text';
}

function asAutomationStatus(value: string): AutomationStatus {
  return ['draft', 'active', 'paused', 'archived'].includes(value) ? value as AutomationStatus : 'draft';
}

function asTriggerType(value: string): AutomationTriggerTriggerTypeEnumKey | null {
  return TRIGGER_TYPE_OPTIONS.some((option) => option.value === value)
    ? value as AutomationTriggerTriggerTypeEnumKey
    : null;
}

function asNodeKind(value: string): AutomationNodeKind | null {
  return ['trigger', 'condition', 'action', 'handoff', 'wait', 'end'].includes(value) ? value as AutomationNodeKind : null;
}

function pointValue(value: unknown): FlowPoint | null {
  const record = asRecord(value);
  return typeof record['x'] === 'number' && typeof record['y'] === 'number'
    ? { x: record['x'], y: record['y'] }
    : null;
}

function asRecord(value: unknown): JsonObject {
  return value && typeof value === 'object' && !Array.isArray(value) ? value as JsonObject : {};
}

function stringArray(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [];
}

function stringValue(value: unknown): string {
  return typeof value === 'string' ? value : '';
}
